import { useCallback } from 'react';
import { useAsyncData, useAsyncMutation } from './useAsyncData';
import { preferencesAPI } from '@/services/api/preferences.api';
import { useAuth } from './useAuth';
import type { LearningPreferences, OnboardingTriggerStatus } from '@/types/preferences.types';

/**
 * useOnboardingPreferencesTrigger
 * Lee el estado del onboarding de preferencias desde el backend y expone la
 * mutation para completarlo. Lo consumen:
 *   - DashboardPage: para decidir si pintar el banner de "personaliza tu
 *     experiencia" (depende de `status.hasSavedCategories`).
 *   - OnboardingPreferencesPage: para guardar el set de categorías al final
 *     del wizard (`complete(slugs)`).
 *
 * Toda la lógica (umbral de stories, cooldown de skips, etc.) vive en el
 * backend — el cliente solo respeta los flags del response.
 */
export function useOnboardingPreferencesTrigger() {
  const { user } = useAuth();
  const isStudent = user?.role === 'student';

  const status = useAsyncData<OnboardingTriggerStatus>({
    fetchFn: () => preferencesAPI.getOnboardingStatus(),
    enabled: isStudent,
    dependencies: [user?.id],
  });

  const completeMutation = useAsyncMutation({
    mutationFn: async (categorySlugs: string[]) => {
      const current = await preferencesAPI.get();
      // Lectura retrocompatible: preservamos los contadores de skip vengan
      // del esquema nuevo (`onboarding.*`) o del viejo plano (`onboarding*`).
      const lp = (current.learningPreferences ?? {}) as Partial<LearningPreferences> & {
        onboardingSkippedCount?: number;
        onboardingSkippedAt?: number;
      };
      const prevSkippedCount = lp.onboarding?.skippedCount ?? lp.onboardingSkippedCount ?? 0;
      const prevSkippedAt = lp.onboarding?.skippedAt ?? lp.onboardingSkippedAt;
      const nextLp: LearningPreferences = {
        categorySlugs,
        onboarding: {
          status: 'completed',
          completedAt: Date.now(),
          skippedCount: prevSkippedCount,
          ...(prevSkippedAt ? { skippedAt: prevSkippedAt } : {}),
        },
      };
      await preferencesAPI.patch({ learningPreferences: nextLp });
    },
    onSuccess: () => {
      void status.refetch();
    },
  });

  const complete = useCallback(
    (slugs: string[]) => completeMutation.mutate(slugs),
    [completeMutation],
  );

  return {
    status: status.data,
    isLoading: status.isLoading,
    refetch: status.refetch,
    complete,
    isCompleting: completeMutation.isLoading,
  };
}
